import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiProblem } from '../api/problem.js';
import { useAnalyticsSummary } from '../analytics/analytics-api.js';
import { ApiErrorView } from '../components/api-error-view.js';
import { CARD_SURFACE } from '../components/card.js';
import { PageHeader } from '../components/page-header.js';
import { formatHours } from '../requests/format.js';
import { useRequestCount } from '../requests/requests-api.js';

const NO_FILTERS = {};

/**
 * Panel del responsable (W6), solo con contadores (los gráficos están en `/analitica`): las pendientes
 * asignadas y las solicitudes del equipo (páginas de un elemento: solo `total`) y el resumen sin filtros
 * (`total` y `approvedHours` tal como llegan, sin recalcular). Un 403 reemplaza la pantalla. Con el aspecto
 * de los conteos de administración: cabecera y cada conteo en su tarjeta-enlace.
 */
export function ManagerPanelPage() {
  const pending = useRequestCount('approvals', 'PENDING_APPROVAL');
  const team = useRequestCount('team');
  const summary = useAnalyticsSummary(NO_FILTERS);

  const denied = [pending.error, team.error, summary.error].find((error) => error instanceof ApiProblem && error.status === 403);
  if (denied) return <ApiErrorView error={denied} />;

  return (
    <div className="space-y-7 lg:px-6 lg:py-4">
      <PageHeader title="Panel del responsable" description="Pendientes asignadas, solicitudes del equipo y horas aprobadas." />
      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Counter label="Pendientes asignadas" to="/aprobaciones?estado=PENDING_APPROVAL" result={pending} />
        <Counter label="Solicitudes del equipo" to="/mi-equipo" result={team} />
        <Counter label="Solicitudes (resumen)" to="/analitica" result={{ ...summary, data: summary.data?.total }} />
        <Counter label="Horas aprobadas" to="/analitica" result={{ ...summary, data: summary.data?.approvedHours }} format={formatHours} />
      </ul>
    </div>
  );
}

function Counter({
  label,
  to,
  result,
  format = String,
}: {
  label: string;
  to: string;
  result: { isPending: boolean; isError: boolean; error: unknown; data: number | undefined };
  format?: (value: number) => string;
}) {
  let value: ReactNode = '…';
  if (result.isError) value = '—';
  else if (result.data !== undefined) value = format(result.data);
  return (
    <li>
      <Link
        to={to}
        className={`block h-full p-5 transition-colors hover:border-ring hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none ${CARD_SURFACE}`}
        aria-label={`${label}: ${value}`}
      >
        <span className="block text-sm text-muted-foreground">{label}</span>
        <span className="mt-2 block text-3xl font-semibold tracking-tight">{value}</span>
      </Link>
      {result.isError ? (
        <div className="mt-2">
          <ApiErrorView error={result.error} />
        </div>
      ) : null}
    </li>
  );
}
